// Types et petits composants partagés par les écrans « Nomenclature et devis ».
import { useEffect, useRef, useState, useSyncExternalStore, type InputHTMLAttributes } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '../../components/ui.tsx';
import { date, euros } from '../../lib/format.ts';

export const SECTIONS = ['materiel', 'accessoire', 'licence', 'pose', 'cablage', 'programmation', 'mise_en_service', 'essais',
  'documentation', 'deplacement', 'sous_traitance', 'frais', 'autre'] as const;
export type Section = typeof SECTIONS[number];
export const LIBELLES_SECTIONS: Record<Section, string> = {
  materiel: 'Matériel', accessoire: 'Accessoires', licence: 'Licences', pose: 'Pose', cablage: 'Câblage',
  programmation: 'Programmation', mise_en_service: 'Mise en service', essais: 'Essais', documentation: 'Documentation',
  deplacement: 'Déplacements', sous_traitance: 'Sous-traitance', frais: 'Frais', autre: 'Autres postes',
};
export const OPTIONS_SECTIONS = SECTIONS.map((s) => ({ valeur: s, libelle: LIBELLES_SECTIONS[s] }));

export const LIBELLES_MOTIFS: Record<string, string> = {
  cout_unitaire: 'Coût unitaire à renseigner',
  heures_a_renseigner: 'Temps de pose à renseigner',
  taux_horaire_absent: 'Taux horaire à choisir pour les heures saisies',
  taux_horaire_inconnu: 'Taux horaire introuvable ou inactif',
  cout_horaire_inconnu: 'Coût horaire du taux à renseigner',
  taux_marge_invalide: 'Taux de marge égal ou supérieur à 100 %',
};

export interface Calcul {
  id: string; complet: boolean; a_renseigner: string[];
  cout_materiel?: string | null; cout_mo?: string | null; cout_horaire?: string | null; cout_revient?: string | null;
  taux_applique?: string; taux_origine?: 'ligne' | 'revision';
  prix_unitaire_ht: string | null; total_ht: string | null; marge?: string | null; marge_pct?: string | null; majoration_pct?: string | null;
  /** Prix de vente unitaire HT convenu (document signé ou commandé) : le prix ne dépend pas du coût. */
  prix_convenu?: string | null; cout_a_renseigner?: string[];
}
export interface Ligne {
  id: string; position: number; section: Section; label: string; manufacturer: string | null; reference: string | null;
  quantity: string; unit: string; unit_cost?: string | null; cost_source?: string | null; cost_date?: string | null;
  labor_hours: string | null; labor_rate_code?: string | null; rate_override?: string | null; requirement_id: string | null;
  requirement_label?: string | null; price_item_id?: string | null; calcul: Calcul;
  sale_unit_price?: string | null;
  /** Source de la quantité (chiffrage proposé : ligne du DPGF, comptage du CCTP, métré, saisie à la main) ; null = inconnue. */
  quantity_source?: string | null;
}
/** Proposition de mise à jour d'une ligne (jamais appliquée sans clic). */
export interface PropositionPrix {
  price_item_id: string; date: string | null; prix: string; fournisseur: string | null; source_libelle: string; motif: 'plus_recent' | 'corrige';
}
export interface AlertePrixLigne { ancien: boolean; age_mois: number | null; proposition: PropositionPrix | null; nb_fournisseurs: number }
export interface AlertesPrix { seuil_mois: number; lignes: Record<string, AlertePrixLigne> }
export interface Bloc {
  ht_brut: string; remise: string; ht_net: string; tva: string; ttc: string;
  /** null : une ligne à prix convenu a un coût inconnu — coût total et marge inconnus. */
  cout_revient?: string | null; marge?: string | null; marge_pct?: string | null; majoration_pct?: string | null;
}
export interface Totaux {
  complet: boolean; nb_lignes: number; nb_a_renseigner: number; lignes_a_renseigner: { id: string; label: string; motifs: string[] }[];
  mode?: 'marge' | 'majoration'; taux_defaut?: string; remise_pct: string; taux_tva: string;
  totaux: Bloc | null; partiel: (Bloc & { libelle: string }) | null;
}
export interface DroitsDevis { couts: boolean; ecrire: boolean; valider: boolean; emettre: boolean; derogation: boolean; prix: boolean; accepter?: boolean; preparer_chantier?: boolean }
export interface RevisionResume { id: string; rev_no: number; variant: string; status: string; emitted_at: string | null }
export interface VueRevision {
  revision: {
    id: string; quote_id: string; rev_no: number; variant: string; status: string; pricing_mode?: 'marge' | 'majoration'; default_rate?: string;
    discount_pct: string; vat_rate: string; hypotheses: string | null; exclusions: string | null; validity_days: number;
    validated_at: string | null; validated_by_name: string | null; emitted_at: string | null; emitted_by_name: string | null;
    created_at: string; updated_at: string; fige: boolean;
  };
  devis: { id: string; number: string; title: string; dossier_id: string };
  dossier: { id: string; reference: string; title: string; demo: boolean };
  lignes: Ligne[];
  totaux: Totaux;
  derogation: { motif: string; auteur: string; date: string; lignes: { id: string; label: string; motifs: string[] }[] } | null;
  /** « Acceptée par le client » (null = non acceptée). */
  acceptation?: import('./acceptation.tsx').Acceptation | null;
  /** Chantier né de ce devis et de cette variante (null = aucun). */
  chantier?: { id: string; reference: string | null; status: string; depuis_cette_revision: boolean } | null;
  revisions: RevisionResume[];
  droits: DroitsDevis;
  taux_horaires: { code: string; label: string; hourly_cost: string | null; active: boolean }[];
  /** Alertes de prix (révision non figée, rôle qui voit les coûts) ; null sinon. */
  alertes_prix?: AlertesPrix | null;
}

/** Libellé du bouton de mise à jour : la date du tarif proposé, toujours visible avant le clic. */
export const libelleMiseAJour = (p: PropositionPrix) =>
  `Mettre à jour au tarif du ${p.date ? date(p.date) : 'date inconnue'}${p.motif === 'corrige' ? ' (corrigé)' : ''}`;

/** Badges d'alerte d'une ligne : prix ancien, tarif plus récent ou corrigé. */
export function BadgesAlertePrix({ a, seuil }: { a?: AlertePrixLigne; seuil: number }) {
  if (!a || (!a.ancien && !a.proposition)) return null;
  return (
    <span className="devis-alertes-prix">
      {a.ancien && <Badge ton="ambre" titre={`Prix daté de plus de ${seuil} mois`}>{`Prix de ${a.age_mois ?? seuil} mois`}</Badge>}
      {a.proposition && <Badge ton="info" titre={a.proposition.source_libelle}>
        {a.proposition.motif === 'corrige' ? 'Prix source corrigé' : `Tarif du ${a.proposition.date ? date(a.proposition.date) : '?'} disponible`}
      </Badge>}
    </span>
  );
}

/** Montant : euros() ; inconnu → badge ambre « À renseigner », jamais 0. */
export function Montant({ v, fort }: { v: string | null | undefined; fort?: boolean }) {
  const t = euros(v);
  if (t === null) return <BadgeARenseigner />;
  return <span className={`chiffre ${fort ? 'devis-fort' : ''}`}>{t}</span>;
}

export function BadgeARenseigner({ titre }: { titre?: string }) {
  return <Badge ton="ambre" icone={<AlertTriangle size={14} aria-hidden />} titre={titre}>À renseigner</Badge>;
}

export const pct = (v: string | null | undefined) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`);
export const quantite = (v: string) => Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 4 });
/** Heures : null = temps de pose à renseigner (jamais affiché 0). */
export const heures = (v: string | null | undefined) => (v === null || v === undefined ? 'À renseigner' : `${quantite(v)} h`);
/** Chaîne décimale serveur → saisie française (« 12.5000 » → « 12,5 »). */
export const versSaisie = (v: string | null | undefined) => (v === null || v === undefined ? '' : String(Number(v)).replace('.', ','));
/** La saisie est-elle un nombre complet (sinon on attend la fin de la frappe) ? */
export const saisieValide = (s: string) => s.trim() === '' || /^-?[\d\s  ]*([.,]\d+)?\s*€?$/.test(s.trim()) && /\d/.test(s);
export const sourcePrix = (l: { cost_source?: string | null; cost_date?: string | null }) =>
  [l.cost_source, l.cost_date ? date(l.cost_date) : null].filter(Boolean).join(' — ');

/** Champ de saisie qui garde la frappe en cours même si les données serveur se rafraîchissent, et enregistre
 *  à la sortie du champ (ou après une pause de frappe). */
export function SaisieAuto({ valeur, enregistrer, montantFr, ...p }:
  { valeur: string; enregistrer: (v: string) => Promise<unknown> | void; montantFr?: boolean } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [local, setLocal] = useState(valeur);
  const [erreur, setErreur] = useState(false);
  const focus = useRef(false);
  const minuterie = useRef<number | undefined>(undefined);
  useEffect(() => { if (!focus.current) setLocal(valeur); }, [valeur]);
  const valider = async (v: string) => {
    window.clearTimeout(minuterie.current);
    if (v === valeur) { setErreur(false); return; }
    if (montantFr && !saisieValide(v)) { setErreur(true); return; }
    try { await enregistrer(v); setErreur(false); } catch { setErreur(true); }
  };
  return (
    <input {...p} className={`saisie ${p.className ?? ''}`} value={local} aria-invalid={erreur || undefined}
      inputMode={montantFr ? 'decimal' : p.inputMode}
      onFocus={() => { focus.current = true; }}
      onBlur={() => { focus.current = false; void valider(local); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setLocal(valeur); setErreur(false); } }}
      onChange={(e) => {
        const v = e.target.value; setLocal(v);
        window.clearTimeout(minuterie.current);
        if (!montantFr || saisieValide(v)) minuterie.current = window.setTimeout(() => { void valider(v); }, 1200);
      }} />
  );
}

/** Vrai sous une largeur donnée (bascule tableau ↔ cartes sans dupliquer les champs dans la page). */
export function useEtroit(px = 900) {
  const requete = `(max-width: ${px}px)`;
  return useSyncExternalStore(
    (cb) => { const mq = window.matchMedia(requete); mq.addEventListener('change', cb); return () => mq.removeEventListener('change', cb); },
    () => window.matchMedia(requete).matches,
    () => false,
  );
}
